import {
  BaseResponse,
  CrudService,
  HttpService,
} from '@monorepo/client-common';
import {
  Scene,
  Asset,
  SubtitlesConfig,
  Video,
  Timezone,
  SocialAccountMetadata,
  Languages,
  Word,
  ClientUser,
  ThumbnailConfig,
  SceneItem,
  PublishedAccount,
} from '@monorepo/types';

export interface UpdateSocialPublishParams {
  videoId: string;
  publishedAccounts: PublishedAccount[];
  timezone: Timezone;
  publishAt?: Date;
}

const routeName = 'video';

class VideoService extends CrudService<Video> {
  protected override route = routeName;

  constructor({ httpService }: { httpService: HttpService }) {
    const route = routeName;

    super({ route: routeName, httpService });

    this.route = route;
  }

  async getScenes(videoId: string) {
    const response = await this.httpService.get<BaseResponse<Scene[]>>(
      `${this.path}/${videoId}/scenes`
    );

    return response.data;
  }

  async getGlobalScene(videoId: string) {
    const response = await this.httpService.get<BaseResponse<Scene>>(
      `${this.path}/${videoId}/global-scene`
    );

    return response.data;
  }

  async getProgress(videoId: string) {
    const response = await this.httpService.get<
      BaseResponse<
        Pick<
          Video,
          | 'progress'
          | 'isRendering'
          | 'renderProgress'
          | 'renderRequested'
          | 'renderStartDate'
        >
      >
    >(`${this.path}/${videoId}/progress`);

    return response.data;
  }

  async render(videoId: string) {
    const response = await this.httpService.get<BaseResponse<boolean>>(
      `${this.path}/${videoId}/render`
    );

    return response.data;
  }

  async download(videoId: string) {
    const response = await this.httpService.get<Blob>(
      `${this.path}/${videoId}/download`,
      {
        responseType: 'blob',
      }
    );

    return response;
  }

  async updateScene(
    sceneId: string,
    videoId: string,
    updateDto: Partial<Scene>
  ) {
    const response = await this.httpService.post<
      Partial<Scene>,
      BaseResponse<Scene>
    >(`${this.path}/${videoId}/scene/${sceneId}`, updateDto);

    return response.data;
  }

  async createScene(videoId: string, sceneId?: string) {
    let url = `${this.path}/${videoId}/create-scene`;

    if (sceneId) {
      url += `?sceneId=${sceneId}`;
    }

    const response = await this.httpService.post<null, BaseResponse<Scene>>(
      url
    );

    return response.data;
  }

  async deleteScene(videoId: string, sceneId: string) {
    const url = `${this.path}/${videoId}/delete-scene/${sceneId}`;

    const response = await this.httpService.delete<BaseResponse<boolean>>(url);

    return response.data;
  }

  async updateSceneItem({
    sceneId,
    videoId,
    itemIndex,
    updateDto,
  }: {
    sceneId: string;
    videoId: string;
    itemIndex: number;
    updateDto: Partial<SceneItem>;
  }) {
    const response = await this.httpService.post<
      Partial<SceneItem>,
      BaseResponse<Scene>
    >(`${this.path}/${videoId}/scene/${sceneId}/item/${itemIndex}`, updateDto);

    return response.data;
  }

  async editSceneDuration({
    sceneId,
    videoId,
    updateDto,
  }: {
    sceneId: string;
    videoId: string;
    updateDto: Pick<Scene, 'duration'>;
  }) {
    const response = await this.httpService.post<
      Pick<Scene, 'duration'>,
      BaseResponse<Scene>
    >(`${this.path}/${videoId}/scene/${sceneId}/edit-duration`, updateDto);

    return response.data;
  }

  async createSceneItem({
    sceneId,
    videoId,
    createDto,
  }: {
    sceneId: string;
    videoId: string;
    createDto: SceneItem;
  }) {
    const response = await this.httpService.post<
      SceneItem,
      BaseResponse<Scene>
    >(`${this.path}/${videoId}/scene/${sceneId}/item`, createDto);

    return response.data;
  }

  async deleteSceneItem({
    sceneId,
    videoId,
    itemIndex,
  }: {
    sceneId: string;
    videoId: string;
    itemIndex: number;
  }) {
    const response = await this.httpService.delete<BaseResponse<Scene>>(
      `${this.path}/${videoId}/scene/${sceneId}/item/${itemIndex}`
    );

    return response.data;
  }

  async updateGeneralInfo(videoId: string, updateDto: Partial<Video>) {
    const response = await this.httpService.post<
      Partial<Scene>,
      BaseResponse<Scene>
    >(`${this.path}/${videoId}/general-update`, updateDto);

    return response.data;
  }

  async updateThumbnail(videoId: string, updateDto: Partial<ThumbnailConfig>) {
    const response = await this.httpService.post<
      Partial<ThumbnailConfig>,
      BaseResponse<boolean>
    >(`${this.path}/${videoId}/thumbnail-update`, updateDto);

    return response.data;
  }

  async updateSceneWords(sceneId: string, videoId: string, words: Word[]) {
    const response = await this.httpService.post<
      { words: Word[] },
      BaseResponse<Scene>
    >(`${this.path}/${videoId}/scene/${sceneId}/words`, { words });

    return response.data;
  }

  async updateSubtitles(videoId: string, subtitlesConfig: SubtitlesConfig) {
    const response = await this.httpService.post<
      SubtitlesConfig,
      BaseResponse<boolean>
    >(`${this.path}/${videoId}/subtitles`, subtitlesConfig);

    return response.data;
  }

  async updateMetadata(
    videoId: string,
    metadata: Pick<Video, 'title' | 'description'>
  ) {
    const response = await this.httpService.post<
      Pick<Video, 'title' | 'description'>,
      BaseResponse<boolean>
    >(`${this.path}/${videoId}/metadata`, metadata);

    return response.data;
  }

  async updateVoiceover(
    videoId: string,
    voiceoverConfig: { voiceoverId: string }
  ) {
    const response = await this.httpService.post<
      { voiceoverId: string },
      BaseResponse<boolean>
    >(`${this.path}/${videoId}/voiceover`, voiceoverConfig);

    return response.data;
  }

  async updateBackgroundMusic(
    videoId: string,
    musicId?: string,
    backgroundMusicVolume?: number
  ) {
    const response = await this.httpService.post<
      { musicId?: string; backgroundMusicVolume?: number },
      BaseResponse<boolean>
    >(`${this.path}/${videoId}/background-music`, {
      musicId,
      backgroundMusicVolume,
    });

    return response.data;
  }

  async generateScript({
    text,
    languageCode,
    duration,
  }: {
    text: string;
    languageCode: Languages;
    duration: number;
  }) {
    const response = await this.httpService.post<
      {
        text: string;
        languageCode: Languages;
        duration: number;
      },
      BaseResponse<string>
    >(`${this.path}/script-writer`, { text, languageCode, duration });

    return response.data;
  }

  async updateSocialPublish({
    videoId,
    timezone,
    publishAt,
    publishedAccounts,
  }: UpdateSocialPublishParams) {
    const response = await this.httpService.post<
      Omit<UpdateSocialPublishParams, 'videoId'>,
      BaseResponse<boolean>
    >(`${this.path}/${videoId}/social-publish`, {
      publishAt,
      timezone,
      publishedAccounts,
    });

    return response.data;
  }

  async cancelSchedule({ videoId }: { videoId: string }) {
    const response = await this.httpService.post<null, BaseResponse<boolean>>(
      `${this.path}/${videoId}/cancel-schedule`
    );

    return response.data;
  }

  async uploadMusic({
    videoId,
    sceneId,
    itemIndex,
    file,
  }: {
    sceneId: string;
    itemIndex: number;
    videoId: string;
    file: FormData;
  }) {
    const response = await this.httpService.post<
      FormData,
      BaseResponse<{ url: string; name: string }>
    >(
      `${this.path}/upload-music/${videoId}/scene/${sceneId}/item/${itemIndex}`,
      file,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  }

  async getVideosByDateRange(startDate: string, endDate: string) {
    const response = await this.httpService.get<BaseResponse<Video[]>>(
      `${this.path}/by-date-range?startDate=${startDate}&endDate=${endDate}`
    );

    return response.data;
  }
}

export default VideoService;
